import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { winnerActions } from './../../state/stores/winner';

import styles from './drawings.module.scss';

export const WinnersLink = ({ drawing = {}, clickHandler }) => {
    const dispatch = useDispatch();
    const winners = useSelector(({ winners }) => winners.winners);

    const fetchWinners = drawing => {
        // NOTE: this check implicitly prevents duplicate fetches for the
        // same draw_number by the presence of an object with a loading flag
        if (!winners[`${drawing.category}-${drawing.draw_number}`]) {
            winnerActions(dispatch).getWinners({
                game_id: 1574,
                draw_number: drawing.draw_number,
                draw_date: drawing.draw_date,
                category: drawing.category,
            });
        }
    };

    return drawing.has_winners !== '0' ? (
        <a
            href={`winners/?drawing-winners=${drawing.draw_number}`}
            className={styles.winnersLink}
            onMouseEnter={() => {
                fetchWinners(drawing);
            }}
            onClick={e => {
                e.preventDefault();
                fetchWinners(drawing);
                clickHandler(drawing);
            }}
        >
            Click here
            <br />
            to view winner{parseInt(drawing.category, 10) === 1 ? 's' : ''}
        </a>
    ) : (
        '-'
    );
};

WinnersLink.propTypes = {
    drawing: PropTypes.object.isRequired,
    clickHandler: PropTypes.func.isRequired,
};

export const WinnersTable = ({ winners = [], drawName = '' }) => {
    const getPrizeName = drawing => {
        return drawing == 'Final Drawing'
            ? 'One (1) $10,000'
            : 'Forty (40) $500';
    };

    const groupByPrize = winnersArray => {
        return winnersArray.reduce((grouped, current) => {
            // add this prize_type if we don't have it already
            if (!grouped[current.prize_type]) {
                grouped[current.prize_type] = [];
            }

            // add the winner to the corresponding prize group
            grouped[current.prize_type].push(current);

            return grouped;
        }, {});
    };

    const groupedWinners = groupByPrize(winners);

    const winnersSort = drawName => {
        // normal sorting when not final
        if (drawName.toLowerCase().indexOf('final') < 0) {
            return (a, b) => a - b;
        } else {
            // final drawing 2 should come last
            return (a, b) => {
                if (a === '2' || b === '2') {
                    return -1;
                } else {
                    return parseInt(a, 10) - parseInt(b, 10);
                }
            };
        }
    };

    // GA only wants winners names for winnings less than $250k
    // prize level 3 should never show the name but prize level 1
    // could potentially be > $250k.  Drawing Services should provide
    // the winner's name as Anonymous (not sure if first or last name)
    // in that scenario we are showing only the city, state info
    const prizeLevelThree = prize_level => prize_level === '3';
    const nameIsAnon = name => name.toLowerCase().indexOf('anonymous') > -1;

    return (
        <>
            {Object.keys(groupedWinners)
                .sort(winnersSort(drawName))
                .map(prize => {
                    const prizeWinners = groupedWinners[prize];
                    // We're only looking for anonymous as the name if prize_level is 1
                    // the other prizes are fixed (there should just be one Jackpot winner) 🤞
                    const hasAnonymous =
                        prize === '1' &&
                        prizeWinners.some(({ first_name, last_name }) => {
                            return (
                                nameIsAnon(first_name) || nameIsAnon(last_name)
                            );
                        });
                    const visibleNames = prizeLevelThree(prize) || hasAnonymous;
                    const prizeName =
                        prize === '1' && prizeWinners.length
                            ? prizeWinners[0].prize
                            : getPrizeName(drawName);

                    return (
                        <div
                            key={`${prize}-prize-group`}
                            className={styles.winnersGroup}
                        >
                            <h3>
                                {`${prizeName} Winner${
                                    prizeWinners.length !== 1 ? 's' : ''
                                } - ${drawName}`}
                            </h3>
                            <table
                                className={`${
                                    visibleNames
                                        ? styles.locationOnly
                                        : styles.nameLocation
                                }`}
                            >
                                <thead>
                                    <tr>
                                        {visibleNames ? null : <th>Name</th>}
                                        <th>City, State</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prizeWinners.map(
                                        (
                                            {
                                                first_name,
                                                last_name,
                                                city,
                                                state,
                                            },
                                            index
                                        ) => (
                                            <tr key={`${index}-${prize}`}>
                                                {visibleNames ? null : (
                                                    <td>
                                                        {first_name}{' '}
                                                        {last_name.charAt(0)}.
                                                    </td>
                                                )}
                                                <td>
                                                    {city}, {state}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
        </>
    );
};

WinnersTable.propTypes = {
    winners: PropTypes.array.isRequired,
    drawName: PropTypes.string,
};

export const WinnersModal = ({
    showModal = false,
    closeModal,
    finalDraw = false,
    draw_number,
    category,
}) => {
    const winners = useSelector(({ winners }) => winners.winners);
    const [selectedWinners, filterWinners] = useState([]);
    const modalStyles = {
        background: 'rgba(0, 0, 0, .4)',
        display: 'flex',
    };

    useEffect(() => {
        if (
            winners[`${category}-${draw_number}`] &&
            winners[`${category}-${draw_number}`].winners
        ) {
            filterWinners(winners[`${category}-${draw_number}`].winners);
        } else {
            filterWinners([]);
        }
    }, [draw_number, winners, category]);

    const drawName =
        draw_number === 11 ? 'Final Drawing' : `Drawing ${draw_number}`;
    const winnerText = drawName =>
        drawName == 'Final Drawing' ? 'Winner' : 'Winners';

    return (
        <div
            className={`modal ${showModal ? 'in' : ''} winners-modal`}
            style={showModal ? modalStyles : { display: 'none' }}
        >
            <Modal.Dialog>
                <Modal.Header>
                    <h2 className={styles.winnersHeader}>
                        {`${winnerText(drawName)} -
                        ${drawName}`}
                    </h2>
                    <button
                        className="btn-close close"
                        aria-label="Close"
                        onClick={closeModal}
                    >
                        X
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.winnersContent}>
                        {selectedWinners.length ? (
                            <WinnersTable
                                winners={selectedWinners}
                                drawName={drawName}
                            />
                        ) : winners[draw_number] &&
                          winners[draw_number]
                              .loading ? /* could replace with loading indicator */ null : (
                            <div className={styles.noWinners}>
                                No winners found for the selected drawing
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal.Dialog>
        </div>
    );
};

WinnersModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    finalDraw: PropTypes.bool.isRequired,
    draw_number: PropTypes.number,
    category: PropTypes.string,
};
